import { Component, forwardRef, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import { TranslateModule } from '@ngx-translate/core';
import { MatRadioModule } from '@angular/material/radio';
import { MatButtonModule } from '@angular/material/button';

import {
  AbstractAuthenticationService,
  AbstractStateService,
  CustomerService,
  DialogService,
  ErrorHandlerServiceService,
  ResendPinCountdownService,
  UserService,
} from '../../services';
import { BaseComponent } from '../../core';
import { COMMUNICATION_WAY, User } from '../../models';
import {
  SuccessCheckAnimationDialogComponent,
  SuccessCheckAnimationDialogComponentData,
} from '../core-layout/success-check-animation-dialog/success-check-animation-dialog.component';
import {
  UpdatePasswordPinDialogComponent,
  UpdatePasswordPinDialogComponentData,
} from '../update-password-pin-dialog';
import {
  EmailPhoneConfirmationPinDialogComponent,
  EmailPhoneConfirmationPinDialogComponentData,
} from '../email-phone-confirmation-pin-dialog';
import {
  ContactDataFormComponent,
  ContactDataFormData,
} from '../contact-data-form';
import { LibCoreLayoutModule } from '../core-layout';
import { PasswordFormComponent } from '../password-form';
import {
  PersonalDataFormComponent,
  PersonalDataFormData,
} from '../personal-data-form';
import { PinCodeInputModule } from '../pin-code-input';

@Component({
  selector: 'lib-user-profile',
  templateUrl: './user-profile.component.html',
  styleUrls: ['./user-profile.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    forwardRef(() => LibCoreLayoutModule),
    // LibCoreLayoutModule,
    TranslateModule,
    ReactiveFormsModule,
    PersonalDataFormComponent,
    ContactDataFormComponent,
    PasswordFormComponent,
    // material design
    MatButtonModule,
    MatRadioModule,
    PinCodeInputModule,
  ],
})
export class UserProfileComponent extends BaseComponent implements OnInit {
  @Input() topOffsetPx = 0;
  @Input() showPinPassword = false;

  public readonly NUMBER_OF_PIN_DIGIT = 6;
  public readonly COMMUNICATION_WAY = COMMUNICATION_WAY;
  public readonly CONTACT_INFO_SECTION_ID = 'contact-info-section';

  public personalDataForm = new FormControl<PersonalDataFormData | null>(null, [
    Validators.required,
  ]);
  public contactForm = new FormControl<ContactDataFormData | null>(null, [
    Validators.required,
  ]);
  public communicationWayForm = new FormControl<COMMUNICATION_WAY | null>(
    null,
    [Validators.required]
  );
  public passwordForm = new FormControl(null, [Validators.required]);
  public updatePinForm = new FormControl(null, [Validators.required]);

  public user: User;
  public isUpdatingPersonalData = false;
  public isConfirmingContactData = false;
  public isUpdatingCommunicationWayData = false;
  public isUpdatingPassword = false;
  public isUpdatingPinCode = false;

  public resendPinCountdownInSecond = 0;

  constructor(
    private errorHandlerServiceService: ErrorHandlerServiceService,
    private stateService: AbstractStateService,
    private dialogService: DialogService,
    private resendPinCountdownService: ResendPinCountdownService,
    private userService: UserService,
    private authenticationService: AbstractAuthenticationService,
    private customerService: CustomerService
  ) {
    super();

    this.stateService.user$
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe((v) => this.updateUser(v));

    this.resendPinCountdownService.resendPinCountdownInSecond$
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe((v) => (this.resendPinCountdownInSecond = v));
  }

  public ngOnInit() {
    this.fetchCurrentLoggedInUser();
  }

  public isAbleToUpdatePersonalData(): boolean {
    return (
      !!this.user &&
      !this.isUpdatingPersonalData &&
      this.personalDataForm.valid &&
      (this.personalDataForm.value.name !== this.user.name ||
        this.personalDataForm.value.surname !== this.user.surname ||
        this.personalDataForm.value.birthday !== this.user.birthday)
    );
  }

  public async updatePersonalData() {
    if (!this.isAbleToUpdatePersonalData()) {
      return;
    }

    try {
      this.isUpdatingPersonalData = true;

      const isSuccess = await this.userService.updateUser({
        ...this.user,
        ...this.personalDataForm.value,
      });

      if (isSuccess) {
        this.displaySuccessCheckAnimationDialog();
        await this.fetchCurrentLoggedInUser();
      }
    } catch (e) {
      this.errorHandlerServiceService.handleError(e);
    } finally {
      this.isUpdatingPersonalData = false;
    }
  }

  public isAbleToConfirmEmail(): boolean {
    return (
      !!this.user &&
      !this.user.emailConfirmed &&
      !this.isConfirmingContactData &&
      this.contactForm.valid &&
      this.resendPinCountdownInSecond <= 0
    );
  }

  public async confirmEmail() {
    if (!this.isAbleToConfirmEmail()) {
      return;
    }

    try {
      this.isConfirmingContactData = true;

      const isUpdateEmail = this.user.email !== this.contactForm.value.email;

      const response = isUpdateEmail
        ? await this.userService.updateEmailIfNotVerified(
            this.user.id,
            this.contactForm.value.email
          )
        : await this.userService.getCodeToConfirmEmail(this.user.email);

      if (response?.id) {
        this.resendPinCountdownService.setResendPinCountdownNow();
        const dialogRef = this.dialogService.openDialog(
          EmailPhoneConfirmationPinDialogComponent,
          this.dialogService.createDefaultDialogConfig({
            challengeId: response?.id,
            username: this.contactForm.value.email,
            isConfirmEmail: true,
          } as EmailPhoneConfirmationPinDialogComponentData)
        );

        dialogRef
          .afterClosed()
          .pipe(takeUntil(this.componentDestroyed))
          .subscribe(() => this.fetchCurrentLoggedInUser());
      }
    } catch (e) {
      this.errorHandlerServiceService.handleError(e);
    } finally {
      this.isConfirmingContactData = false;
    }
  }

  public isAbleToConfirmPhoneNumber(): boolean {
    return (
      !!this.user &&
      this.contactForm.valid &&
      (!this.user.phoneNrConfirmed ||
        this.user.phoneNr !== this.contactForm.value.phoneNr) &&
      !this.isConfirmingContactData &&
      this.resendPinCountdownInSecond <= 0
    );
  }

  public async confirmPhoneNumber() {
    if (!this.isAbleToConfirmPhoneNumber()) {
      return;
    }

    try {
      this.isConfirmingContactData = true;

      const isUpdatePhoneNr =
        this.user.phoneNr !== this.contactForm.value.phoneNr;

      const response = isUpdatePhoneNr
        ? await this.userService.updatePhoneNumber(
            this.user.id,
            this.contactForm.value.phoneNr
          )
        : await this.userService.getCodeToConfirmPhoneNumber(this.user.phoneNr);

      if (response?.id) {
        this.resendPinCountdownService.setResendPinCountdownNow();
        const dialogRef = this.dialogService.openDialog(
          EmailPhoneConfirmationPinDialogComponent,
          this.dialogService.createDefaultDialogConfig({
            challengeId: response?.id,
            username: this.contactForm.value.phoneNr,
            isConfirmEmail: false,
          } as EmailPhoneConfirmationPinDialogComponentData)
        );

        dialogRef
          .afterClosed()
          .pipe(takeUntil(this.componentDestroyed))
          .subscribe(() => this.fetchCurrentLoggedInUser());
      }
    } catch (e) {
      this.errorHandlerServiceService.handleError(e);
    } finally {
      this.isConfirmingContactData = false;
    }
  }

  public isAbleToUpdateCommunicationWay(): boolean {
    return (
      !!this.user &&
      !this.isUpdatingCommunicationWayData &&
      (!!this.user.communicationWay || !!this.communicationWayForm.value) &&
      this.user.communicationWay !== this.communicationWayForm.value
    );
  }

  public async updateCommunicationWay() {
    if (!this.isAbleToUpdateCommunicationWay()) {
      return;
    }

    if (
      this.communicationWayForm.value === COMMUNICATION_WAY.SMS &&
      !this.user.phoneNrConfirmed
    ) {
      this.resetSelectedCommunicationWayAndScrollToContactInfoSection();
      return;
    }

    if (
      this.communicationWayForm.value === COMMUNICATION_WAY.EMAIL &&
      !this.user.emailConfirmed
    ) {
      this.resetSelectedCommunicationWayAndScrollToContactInfoSection();
      return;
    }

    try {
      this.isUpdatingCommunicationWayData = true;

      const isSuccess = await this.userService.updateUser({
        ...this.user,
        communicationWay: this.communicationWayForm.value as COMMUNICATION_WAY,
      });

      if (isSuccess) {
        this.displaySuccessCheckAnimationDialog();
        await this.fetchCurrentLoggedInUser();
      }
    } catch (e) {
      this.errorHandlerServiceService.handleError(e);
    } finally {
      this.isUpdatingCommunicationWayData = false;
    }
  }

  public isAbleToUpdatePassword(): boolean {
    return (
      !!this.user &&
      !this.isUpdatingPassword &&
      this.passwordForm.valid &&
      this.resendPinCountdownInSecond <= 0
    );
  }

  public async requestUpdatePassword() {
    if (!this.isAbleToUpdatePassword()) {
      return;
    }

    try {
      this.isUpdatingPassword = true;
      const username = !this.user.email ? this.user.phoneNr : this.user.email;

      const requestId =
        await this.authenticationService.createChangePasswordRequest(
          username,
          this.passwordForm.value
        );

      if (requestId?.length) {
        this.resendPinCountdownService.setResendPinCountdownNow();
        const dialogRef = this.dialogService.openDialog(
          UpdatePasswordPinDialogComponent,
          this.dialogService.createDefaultDialogConfig({
            username,
            password: this.passwordForm.value,
            passwordRequestId: requestId,
            isUsernameEmail: !!this.user.email,
          } as UpdatePasswordPinDialogComponentData)
        );
        this.passwordForm.reset();

        dialogRef
          .afterClosed()
          .pipe(takeUntil(this.componentDestroyed))
          .subscribe(() => this.fetchCurrentLoggedInUser());
      }
    } catch (e) {
      this.errorHandlerServiceService.handleError(e);
    } finally {
      this.isUpdatingPassword = false;
    }
  }

  public isAbleToUpdatePin(): boolean {
    return !!this.user && !this.isUpdatingPinCode && this.updatePinForm.valid;
  }

  public async updatePIN() {
    if (!this.isAbleToUpdatePin()) {
      return;
    }

    try {
      this.isUpdatingPinCode = true;

      const pinToSubmit = this.updatePinForm.value;

      const status = await this.userService.changePinOfUser(
        this.user.id,
        pinToSubmit
      );

      if (status) {
        await this.authenticationService.logout();
        this.updatePinForm.reset();
      }
    } catch (e) {
      this.errorHandlerServiceService.handleError(e);
    } finally {
      this.isUpdatingPinCode = false;
    }
  }

  private resetSelectedCommunicationWayAndScrollToContactInfoSection(): void {
    this.communicationWayForm.setValue(this.user?.communicationWay || null);

    const element = document.getElementById(this.CONTACT_INFO_SECTION_ID);

    if (element) {
      this.stateService.setScrollToPosition(
        element.offsetTop - this.topOffsetPx
      );
    }
  }

  private updateUser(user: User): void {
    this.user = user;
    this.communicationWayForm.setValue(user?.communicationWay || null);
  }

  private async fetchCurrentLoggedInUser() {
    try {
      const user = await this.userService.getCurrentLoggedInUser();
      this.stateService.setUser(user);

      // refresh all exchange tokens so that BE-customer-service can sync user-profile
      await this.customerService.refreshAllExchangeTokensIfGiven();
    } catch (e) {
      this.errorHandlerServiceService.handleError(e);
    }
  }

  private displaySuccessCheckAnimationDialog(): void {
    this.dialogService.openDialog(
      SuccessCheckAnimationDialogComponent,
      this.dialogService.createSmallDialogConfig({
        text: 'user-profile.text.update-success',
      } as SuccessCheckAnimationDialogComponentData)
    );
  }
}
